import { ComposerFilesState } from 'components/ComposerMediaContext'
import { parse } from 'date-fns'
import {
  ARTICLE_TYPE,
  PHOTO_TYPE,
  PostBase,
  STATUS_TYPE,
  Video,
  VIDEO_TYPE
} from 'interfaces'
import {
  BRAND_BLUESKY,
  BRAND_THREADS,
  BRAND_TIKTOK,
  BRAND_YOUTUBE,
  BRAND_FACEBOOK,
  BRAND_INSTAGRAM,
  BRAND_LINKEDIN,
  BRAND_PINTEREST,
  BRAND_TWITTER,
  BRAND_GOOGLE
} from 'shared'
import {
  COMPOSER_STATUS_KEY_GENERIC,
  ComposerBasePost,
  ComposerBlueskyPost,
  ComposerFacebookPost,
  ComposerGooglePost,
  ComposerInstagramPost,
  ComposerPinterestPost,
  ComposerTikTokPost,
  ComposerTwitterPost,
  ComposerYoutubePost,
  CreatePostRequestData,
  GOOGLE_POST_TYPE_UPDATE,
  GooglePostButtonType,
  PINTEREST_AI_SYNTHETIC_PERFORMER,
  RepeatPeriod
} from 'shared/types'
import { StoreThunkAction } from 'store/state'
import { buildHTMLWithTags, getTextFromHtml } from 'utils/composer'
import { resetComposer } from '../actions'
import { getPostData } from '../create/createPost'
import { composerPostEditSelector, composerScheduleSelector } from '../selectors'
import ComposerState, { defaultGoogleEventEndTime, defaultScheduleTimeString, initialState, PostEditType } from '../state'
import { randomId } from '../utils'
import { V1 } from './net'
import { REPEAT_PERIOD_WEEKS } from 'shared'

export function updatePost(files?: ComposerFilesState, post?: CreatePostRequestData, reset?: boolean): StoreThunkAction<any> {
  return (dispatch, getState) => {
    const isScheduled = composerScheduleSelector(getState()).isActive
    const data = post || dispatch(getPostData(isScheduled ? undefined : 'queue', isScheduled, files))
    const postEdit = composerPostEditSelector(getState()) as any
    if (reset) {
      dispatch(resetComposer())
    }
    return dispatch(V1.updatePost(postEdit.id, data, postEdit.bucketId))
  }
}

export function getDerivedStateFromPost(
  post: PostBase,
  type: 'planned' | 'posted' | 'failed' = 'planned',
  plannedPostType?: PostEditType
): Partial<ComposerState> {
  const { contentType, network, details } = post

  const state: Partial<ComposerState> = {
    postEdit: type === 'planned'
      ? { id: post.id, bucketId: post.bucket?.id, ppid: post.ppPageId, type: plannedPostType || 'planned' }
      : undefined,
    activeTab: network,
    recycle: post.recycle,
    selectedProfiles: [post.profileId],
    selectedBucketId: undefined,
    altText: post.altText || '',
    altTextEnabled: Boolean(post.altText),
    // TikTok returns the flag on the profile's settings, every other network under `details`. Any Pinterest mode is
    // AI content - "AI modified" is the flag itself, and an AI-generated person implies it.
    aiContent: Boolean(
      details?.isAiContent
      || details?.settings?.find(s => s.ppid === post.ppPageId)?.isAiContent
      || details?.piAiContentMode
    ),
    posts: getPostsStateByNetwork(post)
  }

  if (details?.documentUrl && details?.documentName) {
    state.documentUpload = { url: details?.documentUrl, name: details?.documentName }
  }

  if (!post.autoPost && type === 'planned') {
    const dateTime = parse(post.dateString, 'dd.LLL.yy hh:mm a', new Date()).toISOString()
    state.schedule = {
      isActive: true,
      time: dateTime,
      date: dateTime,
      repeat: Boolean(post.repeatLength),
      repeatCount: post.repeatLength || 1,
      repeatPeriod: post.repeatPeriod as RepeatPeriod || REPEAT_PERIOD_WEEKS,
      repeatUntil: post.repeatUntil === 'forever' ? 'forever' : 'date',
      repeatUntilDate: post.repeatUntil === 'forever' ? new Date().toISOString() : post.repeatUntil
    }
  }

  if (network === BRAND_PINTEREST && post.details?.photoAlbum) {
    const board = { ...post.details.photoAlbum, selected: true }
    state.pinterestBoards = { [post.ppPageId]: [board] }
  }

  switch (contentType) {
    case PHOTO_TYPE:
      return {
        ...state,
        images: post.images.reduce((acc, imageUrl, index) => {
          const id = randomId()
          acc[id] = { id, url: imageUrl, order: index + 1 }
          return acc
        }, {} as Record<string, any>),
        contentItem: post.details?.sourceFeedName ? {
          type: PHOTO_TYPE,
          socialLink: post.details.sourcePostSocialLink,
          feed: {
            name: post.details.sourceFeedName,
            type: post.details.sourceFeedType
          },
          imageUrl: post.images[0],
          createdAt: new Date()
        } as any : undefined
      }
    case ARTICLE_TYPE: {
      const contentItem = post.details?.sourceFeedName ? {
        type: ARTICLE_TYPE,
        socialLink: post.details?.sourcePostSocialLink || post.linkUrl as string,
        sourceLink: post.details?.sourceLink || post.linkUrl as string,
        title: post.linkTitle,
        description: post.linkDescription,
        feed: {
          name: post.details.sourceFeedName,
          type: post.details.sourceFeedType
        },
        imageUrl: post.images[0],
        createdAt: new Date()
      } as any : undefined

      return {
        ...state,
        contentItem,
        links: contentItem ? [] : [{
          description: post.linkDescription || '',
          domain: post.linkDomain || '',
          image: post.images,
          images: post.images,
          title: post.linkTitle || '',
          type: 'link',
          url: post.linkUrl || '',
          selected: true
        }]
      }
    }

    case VIDEO_TYPE: {
      if (post.nativeVideoUrl) {
        // EXPL: For an Instagram post `videoThumbnailUrl` holds the custom cover, which the composer
        // keeps in the Instagram post state - seeding it as the video thumbnail as well would leave a
        // stale image in the thumbnail box after the cover is removed.
        const isInstagramCover = Boolean(post.details?.coverUrl) && post.details?.videoThumbnailUrl === post.details?.coverUrl
        return {
          ...state,
          video: {
            id: randomId(),
            url: post.nativeVideoUrl,
            thumbnailUrl: isInstagramCover ? undefined : post.details?.videoThumbnailUrl
          }
        }
      }
      if (post.fbVideoUrl) {
        return {
          ...state,
          contentItem: {
            type: VIDEO_TYPE,
            socialLink: post.details?.sourcePostSocialLink || post.linkUrl as string,
            title: post.linkTitle,
            description: post.linkDescription,
            videoUrl: post.linkUrl,
            imageUrl: post.images[0],
            feed: {
              type: BRAND_FACEBOOK,
              name: post.details?.sourceFeedName
            },
            createdAt: new Date()
          } as Video
        }
      }

      const contentItem = post.details?.sourceFeedName ? {
        type: VIDEO_TYPE,
        socialLink: post.details.sourcePostSocialLink,
        feed: {
          name: post.details.sourceFeedName,
          type: post.details.sourceFeedType
        },
        imageUrl: post.images[0],
        createdAt: new Date()
      } as any : undefined
      return {
        ...state,
        contentItem,
        links: contentItem ? [] : [{
          description: post.linkDescription || '',
          domain: post.linkDomain || '',
          image: post.images,
          images: post.images,
          title: post.linkTitle || '',
          type: 'video_link',
          url: post.linkUrl || '',
          selected: true
        }]
      }
    }

    case STATUS_TYPE:
    default:
      return state
  }
}

function getPostsStateByNetwork(post: PostBase) {
  const { network, status, details, twPostType, ppPageId } = post
  const emptyState = initialState().posts
  const linkedInFormattedText = network === BRAND_LINKEDIN ? details?.text : undefined
  const htmlStatus = buildHTMLWithTags(status, details?.tags || [], network, undefined, linkedInFormattedText)
    .replace(/\n/g, '<br/>').replace(/\r/g, '')
  const postForNetwork: ComposerBasePost = {
    status: {
      text: status,
      html: htmlStatus,
      touched: true
    }
  }

  const initComments = (comments?: any[]) => {
    let commentsFormatted: any[] = []
    if (!comments || comments.length === 0) {
      commentsFormatted = [{ id: randomId(), text: '', html: '', delay: 0 }]
    } else {
      commentsFormatted = comments.map(c => ({
        id: randomId(),
        text: c.text,
        html: c.text,
        delay: c.delay || 0
      }))
    }
    (postForNetwork as ComposerInstagramPost).comments = commentsFormatted
  }

  let description = ''

  switch (network) {
    case BRAND_FACEBOOK:
      const fbPost = postForNetwork as ComposerFacebookPost
      fbPost.albums = details?.photoAlbum
        ? [{ ...details?.photoAlbum, ppid: ppPageId }]
        : []
      fbPost.reshare = Boolean(details?.reshareUrl)

      fbPost.comments = (post.postComments || []).map(c => {
        const html = buildHTMLWithTags(c.text, c.tags || [], network).replace(/\n/g, '<br/>').replace(/\r/g, '')
        return {
          id: randomId(),
          text: getTextFromHtml(html),
          html,
          delay: c.delay || 0
        }
      })
      if (fbPost.comments.length === 0) {
        fbPost.comments = [{ id: randomId(), text: '', html: '', delay: 0 }]
      }
      fbPost.postType = post.details?.postType
      if (post.details?.textBackground) {
        (postForNetwork as ComposerFacebookPost).textBackground = post.details.textBackground
      }
      postForNetwork.status.text = getTextFromHtml(htmlStatus)
      break

    case BRAND_TWITTER:
      (postForNetwork as ComposerTwitterPost).tweetType = twPostType || 'new'
      initComments(post.postComments)
      break

    case BRAND_PINTEREST:
      const desc = post.attributes?.description || ''
      description = desc
      postForNetwork.status.touched = false
      ; (postForNetwork as ComposerPinterestPost).title = post.attributes?.title || ''
      ; (postForNetwork as ComposerPinterestPost).description = desc
      ; (postForNetwork as ComposerPinterestPost).destinationUrl = post.attributes?.url || ''
      // EXPL: only the synthetic performer mode lives on the pin - "AI modified" is restored as the `aiContent` flag
      ; (postForNetwork as ComposerPinterestPost).aiContentMode = details?.piAiContentMode === PINTEREST_AI_SYNTHETIC_PERFORMER
        ? PINTEREST_AI_SYNTHETIC_PERFORMER
        : undefined
      break

    case BRAND_INSTAGRAM:
      const location = post.details?.location
      initComments(post.postComments)
      ; (postForNetwork as ComposerInstagramPost).postType = post.details?.postType
      ; (postForNetwork as ComposerInstagramPost).locations = { [post.ppPageId]: location }
      if (post.details?.photoTags) {
        (postForNetwork as ComposerInstagramPost).tags = post.details?.photoTags
      }
      if (post.details?.coverUrl) {
        (postForNetwork as ComposerInstagramPost).coverUrl = post.details?.coverUrl
      }
      break

    case BRAND_GOOGLE: {
      const gPost = postForNetwork as ComposerGooglePost
      gPost.selectedPostType = (post.details?.postType || GOOGLE_POST_TYPE_UPDATE) as ComposerGooglePost['selectedPostType']

      gPost.update = {
        link: post.details?.buttonUrl || '',
        buttonType: post.details?.buttonType as GooglePostButtonType
      }

      gPost.offer = {
        offer: {
          couponCode: post.details?.couponCode || '',
          redeemOnlineUrl: post.details?.redeemOnlineUrl || '',
          termsConditions: post.details?.termsConditions || ''
        },
        event: {
          title: post.details?.title || '',
          schedule: {
            startDate: post.details?.startDate || defaultScheduleTimeString,
            endDate: post.details?.endDate || defaultGoogleEventEndTime
          }
        }
      }

      gPost.event = {
        title: post.details?.title || '',
        schedule: {
          startDate: post.details?.startDate || defaultScheduleTimeString,
          endDate: post.details?.endDate || defaultGoogleEventEndTime
        }
      }
      break
    }

    case BRAND_TIKTOK: {
      const settings = post.details?.settings?.find(s => s.ppid === ppPageId)
      if (settings) {
        (postForNetwork as ComposerTikTokPost).settings = {
          [ppPageId]: {
            ...settings,
            enableComment: Boolean(settings.enableComment),
            enableDuet: Boolean(settings.enableDuet),
            enableStitch: Boolean(settings.enableStitch)
          }
        }
      }
      const desc = post.description || ''
      description = desc
      postForNetwork.status.touched = false
      ; (postForNetwork as ComposerTikTokPost).title = post.title || ''
      ; (postForNetwork as ComposerTikTokPost).description = desc
      break
    }

    case BRAND_YOUTUBE: {
      const desc = post.description || ''
      description = desc
      initComments(post.postComments)
      postForNetwork.status.touched = false
      ; (postForNetwork as ComposerYoutubePost).description = desc
      ; (postForNetwork as ComposerYoutubePost).title = status
      ; (postForNetwork as ComposerYoutubePost).privacyStatus = post.privacyStatus || details?.privacyStatus || 'public'
      break
    }

    case BRAND_THREADS: {
      initComments(post.postComments)
      break
    }

    case BRAND_LINKEDIN: {
      initComments(post.postComments)
      break
    }

    case BRAND_BLUESKY: {
      if (post.details?.facets) {
        (postForNetwork as ComposerBlueskyPost).facets = post.details.facets
      }
      initComments(post.postComments)
      // Allow video posts for Bluesky
      if (post.nativeVideoUrl) {
        (postForNetwork as ComposerBlueskyPost).videoUrl = post.nativeVideoUrl
      }
      if (post.details?.videoThumbnailUrl) {
        (postForNetwork as ComposerBlueskyPost).videoThumbnailUrl = post.details.videoThumbnailUrl
      }
      break
    }
  }

  return {
    ...emptyState,
    [COMPOSER_STATUS_KEY_GENERIC]: {
      status: {
        text: description || postForNetwork.status.text,
        html: description || postForNetwork.status.html,
        touched: true
      }
    },
    [network]: postForNetwork
  }
}
